<template>
  <div class="frame-page relative overflow-hidden bg-[var(--ce-surface)] text-[var(--ce-text)]">
    <div class="frame-grid pointer-events-none absolute inset-0"></div>
    <div class="frame-orb pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl"></div>

    <div class="relative p-3.5 sm:p-5 lg:p-6">
      <!-- CABEÇALHO -->
      <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div class="flex items-center gap-2">
            <span class="status-dot"></span>
            <p class="text-[9px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
              Financial overview
            </p>
          </div>

          <h3 class="mt-2 text-xl font-semibold tracking-[-0.035em] text-[var(--ce-strong)] sm:text-2xl">
            Visão financeira
          </h3>

          <p class="mt-1 max-w-xl text-xs leading-5 text-[var(--ce-muted)]">
            Conversão, contas e investimentos em uma única visão demonstrativa.
          </p>
        </div>

        <div
          class="tabs-shell inline-flex self-start rounded-full border border-[var(--ce-border)] bg-[var(--ce-glass)] p-1 shadow-sm backdrop-blur-xl"
          role="tablist"
          aria-label="Visualizações financeiras"
        >
          <button
            v-for="(section, index) in sections"
            :key="section.id"
            type="button"
            role="tab"
            :aria-selected="active === index"
            @click="active = index"
            :class="[
              'tab-button inline-flex h-9 items-center gap-2 rounded-full px-3 text-[10px] font-semibold transition sm:px-4',
              active === index
                ? 'tab-active'
                : 'text-[var(--ce-muted)] hover:text-[var(--ce-strong)]'
            ]"
          >
            <span class="material-symbols-outlined text-[15px]">
              {{ section.icon }}
            </span>

            <span>{{ section.label }}</span>
          </button>
        </div>
      </div>

      <!-- CONTEXTO RÁPIDO -->
      <div class="summary-grid mt-4 grid gap-2 sm:mt-5 sm:grid-cols-3">
        <button
          v-for="(section, index) in sections"
          :key="`${section.id}-summary`"
          type="button"
          @click="active = index"
          :class="[
            'summary-card group flex items-center gap-3 rounded-[1.15rem] border p-3 text-left transition duration-300',
            active === index
              ? 'summary-active border-violet-500/20'
              : 'border-[var(--ce-border)] bg-[var(--ce-card)] hover:-translate-y-0.5 hover:border-[var(--ce-border-strong)]'
          ]"
        >
          <div
            :class="[
              'grid h-9 w-9 shrink-0 place-items-center rounded-xl transition',
              active === index
                ? 'bg-violet-500/15 text-violet-600 dark:text-violet-300'
                : 'bg-[var(--ce-chip)] text-[var(--ce-muted)]'
            ]"
          >
            <span class="material-symbols-outlined text-[16px]">
              {{ section.icon }}
            </span>
          </div>

          <div class="min-w-0">
            <p class="truncate text-xs font-semibold text-[var(--ce-strong)]">
              {{ section.label }}
            </p>

            <p class="mt-0.5 truncate text-[9px] text-[var(--ce-muted)]">
              {{ section.description }}
            </p>
          </div>
        </button>
      </div>

      <transition name="panel" mode="out-in">
        <!-- CONVERSOR -->
        <section
          v-if="active === 0"
          key="converter"
          class="mt-4 grid gap-3 sm:mt-5 lg:grid-cols-[.82fr_1.18fr]"
        >
          <div class="panel-card">
            <div class="flex items-center justify-between gap-4">
              <div>
                <p class="panel-kicker">Conversão</p>
                <h4 class="mt-1 text-base font-semibold text-[var(--ce-strong)]">
                  Defina os parâmetros
                </h4>
              </div>

              <div class="accent-icon">
                <span class="material-symbols-outlined text-[17px]">
                  currency_exchange
                </span>
              </div>
            </div>

            <div class="mt-5 grid gap-3">
              <label>
                <span class="field-label">Valor</span>

                <div class="relative">
                  <input
                    v-model.number="amount"
                    type="number"
                    min="0"
                    inputmode="decimal"
                    class="field h-10 w-full rounded-xl px-3 pr-14 text-sm outline-none"
                    placeholder="0,00"
                    aria-label="Valor a converter"
                  />

                  <span class="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] text-[var(--ce-muted-2)]">
                    valor
                  </span>
                </div>
              </label>

              <div class="grid grid-cols-2 gap-3">
                <label>
                  <span class="field-label">De</span>
                  <select
                    v-model="from"
                    class="field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    <option value="USD">USD</option>
                    <option value="BRL">BRL</option>
                    <option value="EUR">EUR</option>
                  </select>
                </label>

                <label>
                  <span class="field-label">Para</span>
                  <select
                    v-model="to"
                    class="field h-10 w-full rounded-xl px-3 text-sm outline-none"
                  >
                    <option value="BRL">BRL</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </label>
              </div>

              <button
                type="button"
                @click="convert"
                class="convert-button inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-xs font-semibold text-white"
              >
                <span class="material-symbols-outlined text-[15px]">sync</span>
                Converter
              </button>
            </div>
          </div>

          <div class="result-card relative overflow-hidden rounded-[1.55rem] border border-violet-500/15 p-5 text-white">
            <div class="result-glow pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full blur-3xl"></div>

            <div class="relative flex h-full flex-col">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="text-[10px] text-violet-200/65">Resultado</p>

                  <p
                    class="mt-2 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl"
                    aria-live="polite"
                  >
                    {{ resultFmt }}
                  </p>

                  <p class="mt-1.5 text-[10px] text-zinc-400">
                    1 {{ from }} =
                    <strong class="text-white">{{ rate.toFixed(4) }}</strong>
                    {{ to }}
                  </p>
                </div>

                <span class="result-badge">
                  {{ from }} → {{ to }}
                </span>
              </div>

              <div class="chart-shell mt-5">
                <div class="flex items-center justify-between">
                  <span class="text-[9px] font-semibold uppercase tracking-[0.12em] text-zinc-500">
                    Variação · 30 dias
                  </span>
                  <span class="text-[9px] text-zinc-600">simulação</span>
                </div>

                <svg viewBox="0 0 100 40" class="mt-3 h-20 w-full text-violet-300">
                  <defs>
                    <linearGradient id="converterArea" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stop-color="#a78bfa" stop-opacity=".24" />
                      <stop offset="100%" stop-color="#a78bfa" stop-opacity="0" />
                    </linearGradient>
                  </defs>

                  <path
                    :d="converterAreaPath"
                    fill="url(#converterArea)"
                    stroke="none"
                  />

                  <path
                    :d="converterPath"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    vector-effect="non-scaling-stroke"
                  />
                </svg>
              </div>
            </div>
          </div>
        </section>

        <!-- CONTAS -->
        <section
          v-else-if="active === 1"
          key="accounts"
          class="mt-5"
        >
          <div class="balance-strip">
            <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p class="text-[9px] font-semibold uppercase tracking-[0.14em] text-violet-100/60">
                  Saldo consolidado
                </p>

                <p
                  class="mt-2 text-3xl font-semibold tracking-[-0.04em]"
                  aria-live="polite"
                >
                  {{ totalSaldoFmt }}
                </p>
              </div>

              <div class="flex gap-2">
                <span class="balance-pill">
                  {{ accounts.length }} contas
                </span>

                <span class="balance-pill">
                  Atualizado agora
                </span>
              </div>
            </div>
          </div>

          <div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="acc in accounts"
              :key="acc.bank"
              class="account-card group"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div
                    class="grid h-9 w-9 place-items-center rounded-xl text-white shadow-sm"
                    :style="{ background: `linear-gradient(135deg, ${acc.colorFrom}, ${acc.colorTo})` }"
                  >
                    <span class="material-symbols-outlined text-[15px]">
                      account_balance
                    </span>
                  </div>

                  <div>
                    <p class="text-xs font-semibold text-[var(--ce-strong)]">
                      {{ acc.bank }}
                    </p>

                    <p class="text-[9px] text-[var(--ce-muted)]">
                      Conta conectada
                    </p>
                  </div>
                </div>

                <span class="text-[var(--ce-muted-2)] transition group-hover:translate-x-0.5">
                  ↗
                </span>
              </div>

              <div class="mt-5 flex items-end justify-between gap-3">
                <div>
                  <p class="text-[9px] text-[var(--ce-muted)]">Saldo</p>
                  <p class="mt-1 text-lg font-semibold tracking-[-0.03em] text-[var(--ce-strong)]">
                    {{ formatBRL(acc.balance) }}
                  </p>
                </div>

                <div
                  class="h-1.5 w-12 rounded-full"
                  :style="{ background: `linear-gradient(90deg, ${acc.colorFrom}, ${acc.colorTo})` }"
                ></div>
              </div>
            </article>
          </div>
        </section>

        <!-- INVESTIMENTOS -->
        <section
          v-else
          key="investments"
          class="mt-5"
        >
          <div class="investment-head">
            <div>
              <p class="panel-kicker">Carteira</p>
              <h4 class="mt-1 text-base font-semibold text-[var(--ce-strong)]">
                Performance dos ativos
              </h4>
            </div>

            <div class="flex items-center gap-2 text-[9px] text-[var(--ce-muted)]">
              <span class="status-dot"></span>
              Mercado atualizado
            </div>
          </div>

          <div class="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <article
              v-for="coin in coins"
              :key="coin.symbol"
              class="investment-card"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="flex items-center gap-3">
                  <div
                    class="h-8 w-8 rounded-full ring-4 ring-[var(--ce-page)]"
                    :style="{ background: coin.color }"
                  ></div>

                  <div>
                    <p class="text-xs font-semibold text-[var(--ce-strong)]">
                      {{ coin.name }}
                    </p>
                    <p class="text-[9px] text-[var(--ce-muted)]">
                      {{ coin.symbol }}
                    </p>
                  </div>
                </div>

                <span
                  :class="coin.change24h < 0 ? 'text-rose-500 bg-rose-500/8' : 'text-emerald-500 bg-emerald-500/8'"
                  class="rounded-full px-2 py-1 text-[9px] font-semibold"
                >
                  {{ coin.change24h }}%
                </span>
              </div>

              <svg
                viewBox="0 0 100 40"
                :class="coin.change24h < 0 ? 'text-rose-500' : 'text-emerald-500'"
                class="mt-3 h-12 w-full"
              >
                <path
                  :d="sparkline(coin)"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  vector-effect="non-scaling-stroke"
                />
              </svg>

              <div class="mt-2 flex items-end justify-between gap-3">
                <div>
                  <p class="text-[9px] text-[var(--ce-muted)]">Preço</p>
                  <p class="mt-1 text-lg font-semibold tracking-[-0.03em] text-[var(--ce-strong)]">
                    {{ formatUSD(coin.price) }}
                  </p>
                </div>

                <p class="text-right text-[8px] leading-4 text-[var(--ce-muted)]">
                  #{{ coin.rank }}<br />
                  {{ coin.cap }}
                </p>
              </div>
            </article>
          </div>
        </section>
      </transition>

      <!-- FOOT DO PREVIEW -->
      <div class="mt-4 flex flex-col gap-2 border-t border-[var(--ce-border)] pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p class="text-[9px] leading-5 text-[var(--ce-muted)]">
          Dados demonstrativos para apresentar a experiência do ControlEase.
        </p>

        <div class="flex items-center gap-2">
          <span class="footer-dot"></span>
          <span class="text-[9px] font-semibold text-[var(--ce-muted)]">
            Interface interativa
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const sections = [
  {
    id: 'converter',
    label: 'Conversor',
    icon: 'currency_exchange',
    description: 'Câmbio e simulações'
  },
  {
    id: 'accounts',
    label: 'Contas',
    icon: 'account_balance',
    description: 'Saldos consolidados'
  },
  {
    id: 'investments',
    label: 'Investimentos',
    icon: 'monitoring',
    description: 'Carteira e mercado'
  }
]

const active = ref(0)

const amount = ref(1)
const from = ref('USD')
const to = ref('BRL')
const rate = ref(5.3809)

const result = computed(() => amount.value * rate.value)

const resultFmt = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: to.value
  }).format(result.value)
)

function convert() {
  const key = `${from.value}_${to.value}`

  const table = {
    USD_BRL: 5.3809,
    BRL_USD: 1 / 5.3809,
    EUR_BRL: 5.80,
    BRL_EUR: 1 / 5.80,
    USD_EUR: 0.93,
    EUR_USD: 1.07
  }

  rate.value = table[key] ?? 1
}

const converterValues = computed(() =>
  Array.from(
    { length: 30 },
    (_, index) =>
      50 +
      Math.sin(index / 3) * 10 +
      index * 0.2
  )
)

const converterPath = computed(() =>
  makePath(converterValues.value)
)

const converterAreaPath = computed(() => {
  const path = makePath(converterValues.value)
  return `${path} L 100,40 L 0,40 Z`
})

const accounts = ref([
  { bank: 'Bradesco', balance: 222, colorFrom: '#ef4444', colorTo: '#7c1932' },
  { bank: 'Itaú', balance: 44, colorFrom: '#f59e0b', colorTo: '#7c3a0d' },
  { bank: 'Nubank', balance: 222, colorFrom: '#a855f7', colorTo: '#3b1e6e' },
  { bank: 'Santander', balance: 222, colorFrom: '#ef4444', colorTo: '#7c1932' },
  { bank: 'Caixa', balance: 222, colorFrom: '#2563eb', colorTo: '#1e40af' },
  { bank: 'Banco do Brasil', balance: 222, colorFrom: '#fbbf24', colorTo: '#713f12' }
])

const coins = ref([
  { name: 'Bitcoin', symbol: 'BTC', change24h: -2.19, vol24: '$44.6B', cap: '$2.15T', rank: 1, price: 107673, color: '#f59e0b' },
  { name: 'Ethereum', symbol: 'ETH', change24h: -4.14, vol24: '$23.5B', cap: '$449.1B', rank: 2, price: 3721.52, color: '#6366f1' },
  { name: 'Tether', symbol: 'USDT', change24h: 0.03, vol24: '$80.1B', cap: '$183.4B', rank: 3, price: 1.00, color: '#10b981' },
  { name: 'XRP', symbol: 'XRP', change24h: -3.22, vol24: '$2.79B', cap: '$145.7B', rank: 4, price: 2.43, color: '#22d3ee' },
  { name: 'BNB', symbol: 'BNB', change24h: -4.93, vol24: '$1.47B', cap: '$142.5B', rank: 5, price: 1035.11, color: '#fbbf24' },
  { name: 'Solana', symbol: 'SOL', change24h: -4.43, vol24: '$4.43B', cap: '$97.9B', rank: 6, price: 177.17, color: '#22c55e' }
])

const totalSaldoFmt = computed(() =>
  formatBRL(
    accounts.value.reduce(
      (sum, account) =>
        sum + account.balance,
      0
    )
  )
)

function formatBRL(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value)
}

function formatUSD(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)
}

function makePath(values) {
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1

  const points = values.map(
    (value, index) => {
      const x =
        (index / (values.length - 1)) * 100

      const y =
        40 -
        ((value - min) / range) * 34 -
        3

      return `${x},${y}`
    }
  )

  return `M ${points[0]} L ${points
    .slice(1)
    .join(' ')}`
}

function sparkline(coin) {
  const down =
    coin.change24h < 0

  const values =
    Array.from(
      { length: 30 },
      (_, index) =>
        50 +
        Math.sin(index / 3) * 10 +
        (down
          ? -index * 0.6
          : index * 0.4)
    )

  return makePath(values)
}
</script>

<style scoped>
.frame-page {
  width: 100%;
}

.frame-grid {
  opacity: 0.28;
  background-image:
    linear-gradient(to right, var(--ce-border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--ce-border) 1px, transparent 1px);
  background-size: 38px 38px;
  mask-image:
    linear-gradient(to bottom, black, transparent 92%);
}

.frame-orb {
  background:
    rgba(124, 58, 237, 0.11);
}

.status-dot,
.footer-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #8b5cf6;
  box-shadow:
    0 0 0 4px rgba(139, 92, 246, 0.10);
}

.footer-dot {
  width: 5px;
  height: 5px;
  background: #22c55e;
  box-shadow:
    0 0 0 4px rgba(34, 197, 94, 0.08);
}

.tab-button {
  white-space: nowrap;
}

.tab-active {
  color: white;
  background:
    linear-gradient(
      135deg,
      #6d28d9,
      #9333ea
    );
  box-shadow:
    0 6px 18px
    rgba(
      124,
      58,
      237,
      0.18
    );
}

.summary-active {
  background:
    linear-gradient(
      135deg,
      rgba(124, 58, 237, 0.09),
      rgba(192, 38, 211, 0.05)
    );
}

.panel-card,
.account-card,
.investment-card,
.investment-head {
  border:
    1px solid
    var(--ce-border);

  background:
    var(--ce-card);

  backdrop-filter:
    blur(18px);
}

.panel-card {
  border-radius:
    1.55rem;

  padding:
    1.25rem;
}

.account-card,
.investment-card {
  border-radius:
    1.25rem;

  padding:
    1rem;

  transition:
    transform
    280ms ease,
    border-color
    280ms ease,
    box-shadow
    280ms ease;
}

.account-card:hover,
.investment-card:hover {
  transform:
    translateY(-3px);

  border-color:
    var(--ce-border-strong);

  box-shadow:
    var(--ce-shadow-soft);
}

.investment-head {
  display:
    flex;

  align-items:
    center;

  justify-content:
    space-between;

  gap:
    1rem;

  border-radius:
    1.3rem;

  padding:
    1rem;
}

.panel-kicker {
  color:
    var(--ce-muted-2);

  font-size:
    .58rem;

  font-weight:
    700;

  letter-spacing:
    .14em;

  text-transform:
    uppercase;
}

.accent-icon {
  display:
    grid;

  width:
    2.5rem;

  height:
    2.5rem;

  place-items:
    center;

  border-radius:
    .75rem;

  color:
    var(--ce-purple);

  background:
    var(--ce-accent-soft);

  box-shadow:
    inset 0 0 0 1px
    rgba(
      124,
      58,
      237,
      0.10
    );
}

.field-label {
  display:
    block;

  margin-bottom:
    .4rem;

  color:
    var(--ce-muted);

  font-size:
    .68rem;

  font-weight:
    500;
}

.field {
  color:
    var(--ce-strong);

  background:
    var(--ce-surface);

  border:
    1px solid
    var(--ce-border);

  transition:
    border-color
    220ms ease,
    box-shadow
    220ms ease;
}

.field:focus {
  border-color:
    rgba(
      124,
      58,
      237,
      0.42
    );

  box-shadow:
    0 0 0 4px
    rgba(
      124,
      58,
      237,
      0.08
    );
}

.convert-button,
.balance-strip {
  background:
    linear-gradient(
      135deg,
      #6d28d9,
      #9333ea 55%,
      #c026d3
    );

  box-shadow:
    0 12px 30px
    rgba(
      124,
      58,
      237,
      0.18
    ),
    inset 0 1px 0
    rgba(
      255,
      255,
      255,
      0.18
    );
}

.convert-button {
  transition:
    transform
    250ms ease,
    box-shadow
    250ms ease;
}

.convert-button:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 16px 34px
    rgba(
      124,
      58,
      237,
      0.24
    );
}

.result-card {
  background:
    radial-gradient(
      circle at 85% 10%,
      rgba(
        139,
        92,
        246,
        0.22
      ),
      transparent 30%
    ),
    #0b0812;
}

.result-glow {
  background:
    rgba(
      139,
      92,
      246,
      0.18
    );
}

.result-badge,
.balance-pill {
  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.10
    );

  border-radius:
    9999px;

  background:
    rgba(
      255,
      255,
      255,
      0.06
    );

  padding:
    .35rem .65rem;

  color:
    #a1a1aa;

  font-size:
    .55rem;

  font-weight:
    700;
}

.chart-shell {
  border:
    1px solid
    rgba(
      255,
      255,
      255,
      0.08
    );

  border-radius:
    1.15rem;

  background:
    rgba(
      255,
      255,
      255,
      0.045
    );

  padding:
    .9rem;
}

.balance-strip {
  border-radius:
    1.4rem;

  padding:
    1.1rem 1.2rem;
}

.panel-enter-active,
.panel-leave-active {
  transition:
    opacity
    240ms ease,
    transform
    240ms ease;
}

.panel-enter-from {
  opacity:
    0;

  transform:
    translateY(8px);
}

.panel-leave-to {
  opacity:
    0;

  transform:
    translateY(-6px);
}

@media (
  max-width:
  639px
) {
  .tabs-shell {
    width:
      100%;

    overflow-x:
      auto;
  }

  .tab-button {
    flex:
      1 0 auto;
  }
}


@media (max-width: 639px) {
  .frame-page {
    min-width: 0;
  }

  .tabs-shell {
    display: flex;
    max-width: 100%;
    scrollbar-width: none;
  }

  .tabs-shell::-webkit-scrollbar {
    display: none;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .panel-card,
  .result-card,
  .balance-strip,
  .investment-head {
    border-radius: 1.2rem;
  }

  .account-card,
  .investment-card {
    border-radius: 1.1rem;
  }
}

@media (max-width: 420px) {
  .tab-button {
    padding-left: .7rem;
    padding-right: .7rem;
    font-size: .58rem;
  }

  .result-card {
    padding: 1rem;
  }

  .chart-shell {
    padding: .7rem;
  }
}


@media (
  prefers-reduced-motion:
  reduce
) {
  .panel-enter-active,
  .panel-leave-active {
    transition:
      none;
  }
}
</style>
